// === Admin: Task Types ===
// Full CRUD page — list, add, edit, soft-delete.
// Ported from old Click2Gro (app/Controllers/Admin/TaskTypesController.php)
// Fields: task_name, task_cost, + 13 boolean option flags controlling which
//         fields appear on task/spray forms.

import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Pagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  Chip,
  Stack,
  FormGroup,
  FormControlLabel,
  Checkbox,
  InputAdornment as MuiInputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { alpha, darken } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import {
  AppTableHead,
  AppTableHeadCell,
  AppTableRow,
  DEFAULT_ROWS_PER_PAGE,
  ROWS_PER_PAGE_OPTIONS,
} from '../components/AppTable';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type FlagKey =
  | 'select_spray' | 'start_time' | 'finish_time' | 'operators_name'
  | 'wind_speed'   | 'ppe_name'   | 'batch_number' | 'chemical_rate'
  | 'assign_operator' | 'assign_block' | 'select_block' | 'select_crop' | 'select_week';

const FLAG_KEYS: FlagKey[] = [
  'select_spray', 'start_time', 'finish_time', 'operators_name', 'wind_speed',
  'ppe_name', 'batch_number', 'chemical_rate', 'assign_operator', 'assign_block',
  'select_block', 'select_crop', 'select_week',
];

const FLAG_LABELS: Record<FlagKey, string> = {
  select_spray:    'Select Spray',
  start_time:      'Start Time',
  finish_time:     'Finish Time',
  operators_name:  "Operator's Name",
  wind_speed:      'Wind Speed',
  ppe_name:        'PPE Name',
  batch_number:    'Batch Number',
  chemical_rate:   'Chemical Rate',
  assign_operator: 'Assign Operator',
  assign_block:    'Assign Block',
  select_block:    'Select Block',
  select_crop:     'Select Crop',
  select_week:     'Select Week',
};

// Grouped for the form UI
const FLAG_GROUPS: { label: string; keys: FlagKey[] }[] = [
  { label: 'Timing',           keys: ['start_time', 'finish_time'] },
  { label: 'Staff & Resources', keys: ['operators_name', 'ppe_name', 'assign_operator'] },
  { label: 'Blocks & Crops',   keys: ['assign_block', 'select_block', 'select_crop', 'select_week'] },
  { label: 'Chemical',         keys: ['select_spray', 'batch_number', 'chemical_rate'] },
  { label: 'Other',            keys: ['wind_speed'] },
];

interface TaskTypeRow {
  readonly id: number;
  readonly task_name: string | null;
  readonly task_cost: number | null;
  readonly select_spray:    number | null;
  readonly start_time:      number | null;
  readonly finish_time:     number | null;
  readonly operators_name:  number | null;
  readonly wind_speed:      number | null;
  readonly ppe_name:        number | null;
  readonly batch_number:    number | null;
  readonly chemical_rate:   number | null;
  readonly assign_operator: number | null;
  readonly assign_block:    number | null;
  readonly select_block:    number | null;
  readonly select_crop:     number | null;
  readonly select_week:     number | null;
  readonly created_by:  string | null;
  readonly created_at:  string | null;
  readonly updated_by:  string | null;
  readonly updated_at:  string | null;
}

type FlagState = Record<FlagKey, boolean>;

interface FormState {
  task_name:  string;
  task_cost:  string;
  flags:      FlagState;
  created_by: string | null;
  created_at: string | null;
  updated_by: string | null;
  updated_at: string | null;
}

const DEFAULT_FLAGS: FlagState = Object.fromEntries(
  FLAG_KEYS.map((k) => [k, false]),
) as FlagState;

const DEFAULT_FORM: FormState = {
  task_name:  '',
  task_cost:  '',
  flags:      DEFAULT_FLAGS,
  created_by: null,
  created_at: null,
  updated_by: null,
  updated_at: null,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getApiUrl(): string {
  return (import.meta.env['VITE_API_URL'] as string | undefined) ?? 'http://localhost:8787';
}

function authHeaders(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

function fmtAuDate(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-AU', { dateStyle: 'short', timeStyle: 'medium', hour12: false }).format(new Date(iso));
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-AU', { dateStyle: 'medium' }).format(new Date(iso));
}

function countEnabledFlags(row: TaskTypeRow): number {
  return FLAG_KEYS.filter((k) => row[k] === 1).length;
}

function rowToFlags(row: TaskTypeRow): FlagState {
  return Object.fromEntries(
    FLAG_KEYS.map((k) => [k, row[k] === 1]),
  ) as FlagState;
}

// ---------------------------------------------------------------------------
// TaskTypesPage
// ---------------------------------------------------------------------------

export function TaskTypesPage() {
  const { session } = useAuth();
  const navigate = useNavigate();

  // List state
  const [rows, setRows]               = useState<TaskTypeRow[]>([]);
  const [total, setTotal]             = useState(0);
  const [page, setPage]               = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [search, setSearch]           = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setPage(0); setDebouncedSearch(search); }, 350);
    return () => clearTimeout(t);
  }, [search]);
  const [loading, setLoading]       = useState(false);
  const [listError, setListError]   = useState<string | null>(null);

  // Form dialog
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId]     = useState<number | null>(null);
  const [form, setForm]         = useState<FormState>(DEFAULT_FORM);
  const [saving, setSaving]     = useState(false);

  // Delete dialog
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Snackbar
  const [snack, setSnack] = useState<{ open: boolean; msg: string; severity: 'success' | 'error' }>({
    open: false, msg: '', severity: 'success',
  });

  const token = session?.access_token ?? '';

  // -------------------------------------------------------------------------
  // Fetch list
  // -------------------------------------------------------------------------
  const fetchRows = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setListError(null);
    try {
      const params = new URLSearchParams({
        page:  String(page + 1),
        limit: String(rowsPerPage),
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      });
      const res = await fetch(`${getApiUrl()}/api/v1/admin/task-types?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json() as { data: TaskTypeRow[]; total: number };
      setRows(json.data ?? []);
      setTotal(json.total ?? 0);
    } catch {
      setListError('Failed to load task types');
    } finally {
      setLoading(false);
    }
  }, [token, page, rowsPerPage, debouncedSearch]);

  useEffect(() => { void fetchRows(); }, [fetchRows]);

  // -------------------------------------------------------------------------
  // Form open/close
  // -------------------------------------------------------------------------
  const openAdd = () => {
    setEditId(null);
    setForm(DEFAULT_FORM);
    setFormOpen(true);
  };

  const openEdit = (row: TaskTypeRow) => {
    setEditId(row.id);
    setForm({
      task_name:  row.task_name ?? '',
      task_cost:  row.task_cost != null ? String(row.task_cost) : '',
      flags:      rowToFlags(row),
      created_by: row.created_by ?? null,
      created_at: row.created_at ?? null,
      updated_by: row.updated_by ?? null,
      updated_at: row.updated_at ?? null,
    });
    setFormOpen(true);
  };

  const closeForm = () => { setFormOpen(false); };

  const setFlag = (key: FlagKey, checked: boolean) => {
    setForm((prev) => ({ ...prev, flags: { ...prev.flags, [key]: checked } }));
  };

  // -------------------------------------------------------------------------
  // Save (add / edit)
  // -------------------------------------------------------------------------
  const handleSave = async () => {
    if (!form.task_name.trim()) return;
    setSaving(true);
    try {
      const url = editId
        ? `${getApiUrl()}/api/v1/admin/task-types/${editId}`
        : `${getApiUrl()}/api/v1/admin/task-types`;

      const flagPayload = Object.fromEntries(
        FLAG_KEYS.map((k) => [k, form.flags[k] ? 1 : 0]),
      );

      const res = await fetch(url, {
        method: editId ? 'PUT' : 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({
          task_name: form.task_name.trim(),
          task_cost: form.task_cost.trim() !== '' ? Number(form.task_cost) : null,
          ...flagPayload,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Unknown error' })) as { error?: string };
        throw new Error(err.error ?? 'Save failed');
      }
      setSnack({ open: true, msg: editId ? 'Task type updated' : 'Task type added', severity: 'success' });
      closeForm();
      void fetchRows();
    } catch (e) {
      setSnack({ open: true, msg: e instanceof Error ? e.message : 'Save failed', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // -------------------------------------------------------------------------
  // Delete
  // -------------------------------------------------------------------------
  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await fetch(`${getApiUrl()}/api/v1/admin/task-types/${deleteId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setSnack({ open: true, msg: 'Task type deleted', severity: 'success' });
      setDeleteId(null);
      void fetchRows();
    } catch {
      setSnack({ open: true, msg: 'Delete failed', severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const totalPages = Math.ceil(total / rowsPerPage);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <>
      <Container maxWidth="xl" sx={{ py: 4 }}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
          <Tooltip title="Back to Dashboard">
            <IconButton onClick={() => navigate('/dashboard')} size="small">
              <BackIcon />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700}>Task Types</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage global task type definitions and their form option flags
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
            Add Task Type
          </Button>
        </Box>

        {/* Toolbar */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search task name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 300 }}
            />
            {search && (
              <Button size="small" onClick={() => { setSearch(''); setPage(0); }}>
                Clear
              </Button>
            )}
            <Box sx={{ ml: 'auto' }}>
              <Chip label={`${total} records`} size="small" variant="outlined" />
            </Box>
          </Box>
        </Paper>

        {/* Error */}
        {listError && <Alert severity="error" sx={{ mb: 2 }}>{listError}</Alert>}

        {/* Table */}
        <Paper sx={{ overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small">
              <AppTableHead>
                <TableRow>
                  <AppTableHeadCell>Task Name</AppTableHeadCell>
                  <AppTableHeadCell>Task Cost</AppTableHeadCell>
                  <AppTableHeadCell>Options Enabled</AppTableHeadCell>
                  <AppTableHeadCell>Updated</AppTableHeadCell>
                  <AppTableHeadCell align="right">Actions</AppTableHeadCell>
                </TableRow>
              </AppTableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={32} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">No task types found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => {
                    const enabledCount = countEnabledFlags(row);
                    const enabledNames = FLAG_KEYS
                      .filter((k) => row[k] === 1)
                      .map((k) => FLAG_LABELS[k])
                      .join(', ');
                    return (
                      <AppTableRow key={row.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {row.task_name ?? '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {row.task_cost != null
                              ? new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(row.task_cost)
                              : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {enabledCount > 0 ? (
                            <Tooltip title={enabledNames}>
                              <Chip
                                label={`${enabledCount} / ${FLAG_KEYS.length}`}
                                size="small"
                                variant="outlined"
                                color="success"
                              />
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.secondary">None</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {fmtDate(row.updated_at)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEdit(row)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => setDeleteId(row.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </AppTableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination bar */}
          <Box sx={(theme) => ({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: theme.palette.mode === 'light'
              ? alpha(theme.palette.primary.main, 0.09)
              : darken(theme.palette.background.paper, 0.35),
            px: 2,
            py: 1.5,
          })}>
            <Typography
              variant="body2"
              sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85 })}
            >
              {loading ? 'Loading…' : total === 0 ? 'No records found'
                : `Showing ${page * rowsPerPage + 1} to ${Math.min((page + 1) * rowsPerPage, total)} of ${total} entries`}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85, whiteSpace: 'nowrap' })}
                >
                  Show
                </Typography>
                <Select
                  size="small"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                  sx={(theme) => ({
                    color: theme.palette.text.primary,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
                    '& .MuiSvgIcon-root': { color: theme.palette.text.primary },
                    '& .MuiSelect-select': { py: 0.5, px: 1 },
                  })}
                >
                  {ROWS_PER_PAGE_OPTIONS.map((n) => (
                    <MenuItem key={n} value={n}>{n}</MenuItem>
                  ))}
                </Select>
              </Box>

              {total > 0 && (
                <Typography
                  variant="body2"
                  sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85, whiteSpace: 'nowrap' })}
                >
                  Page {page + 1} of {totalPages}
                </Typography>
              )}

              {totalPages > 1 && (
                <Pagination
                  count={totalPages}
                  page={page + 1}
                  onChange={(_, p) => setPage(p - 1)}
                  color="primary"
                  size="small"
                  disabled={loading}
                  sx={(theme) => ({
                    '& .MuiPaginationItem-root': {
                      color: theme.palette.text.primary,
                      borderColor: theme.palette.divider,
                      '&:hover': { backgroundColor: theme.palette.action.hover },
                    },
                    '& .Mui-selected': {
                      backgroundColor: `${theme.palette.primary.main} !important`,
                      color: theme.palette.primary.contrastText,
                    },
                    '& .Mui-disabled': { opacity: 0.5 },
                  })}
                />
              )}
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* Add / Edit Dialog */}
      <Dialog open={formOpen} onClose={closeForm} maxWidth="md" fullWidth>
        <DialogTitle>{editId ? 'Edit Task Type' : 'Add Task Type'}</DialogTitle>
        <DialogContent sx={{ pt: 3, overflow: 'visible' }}>
          <Stack spacing={3}>
            {/* Basic fields */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                autoFocus
                label="Task Name"
                fullWidth
                size="small"
                value={form.task_name}
                onChange={(e) => setForm((prev) => ({ ...prev, task_name: e.target.value }))}
                inputProps={{ maxLength: 31 }}
                required
              />
              <TextField
                label="Task Cost"
                size="small"
                sx={{ minWidth: 160 }}
                value={form.task_cost}
                onChange={(e) => setForm((prev) => ({ ...prev, task_cost: e.target.value }))}
                type="number"
                inputProps={{ min: 0, step: '0.01' }}
                InputProps={{
                  startAdornment: (
                    <MuiInputAdornment position="start">$</MuiInputAdornment>
                  ),
                }}
              />
            </Stack>

            {/* Checkbox groups */}
            <Box>
              <Typography variant="subtitle2" fontWeight={600} mb={1}>
                Form Option Flags
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                Enable the fields that should appear when this task type is selected in a task form.
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                {FLAG_GROUPS.map((group) => (
                  <Paper key={group.label} variant="outlined" sx={{ p: 1.5 }}>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                      display="block"
                      mb={0.5}
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                    >
                      {group.label}
                    </Typography>
                    <FormGroup>
                      {group.keys.map((key) => (
                        <FormControlLabel
                          key={key}
                          control={
                            <Checkbox
                              size="small"
                              checked={form.flags[key]}
                              onChange={(e) => setFlag(key, e.target.checked)}
                            />
                          }
                          label={
                            <Typography variant="body2">{FLAG_LABELS[key]}</Typography>
                          }
                        />
                      ))}
                    </FormGroup>
                  </Paper>
                ))}
              </Box>
            </Box>

            {/* Audit Trail — edit mode only */}
            {editId && (
              <Box
                mt={1}
                p={2}
                sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.12), borderRadius: 1 }}
              >
                <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1} sx={{ textTransform: 'uppercase', letterSpacing: 0.8, textAlign: 'center' }}>
                  Audit Trail
                </Typography>
                <Box sx={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.disabled" display="block">Created</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {fmtAuDate(form.created_at)}{form.created_by ? <> &mdash; <strong>{form.created_by}</strong></> : ''}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.disabled" display="block">Updated</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {fmtAuDate(form.updated_at)}{form.updated_by ? <> &mdash; <strong>{form.updated_by}</strong></> : ''}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeForm} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={saving || !form.task_name.trim()}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Task Type</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this task type? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteId(null)} disabled={deleting}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleDelete()}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
